import { useCallback, useRef } from "react";

interface UseScrollToOnFocusOptions {
  /**
   * Position ratio from the top of the viewport (0 = top, 0.5 = middle, 1 = bottom)
   * Default: 1/3 (0.33)
   */
  topRatio?: number;
  /**
   * Scroll behavior
   * Default: "smooth"
   */
  behavior?: ScrollBehavior;
  /**
   * Delay before scrolling in milliseconds (useful for keyboard animation)
   * Default: 100
   */
  delay?: number;
}

/**
 * Hook to scroll an element into position when it's focused
 * Typically used for form inputs to prevent keyboard from covering them
 *
 * @param options - Configuration options
 * @returns A ref to attach to the element and an onFocus handler
 */
export const useScrollToOnFocus = <T extends HTMLElement = HTMLElement>(
  options: UseScrollToOnFocusOptions = {},
) => {
  const { topRatio = 1 / 3, behavior = "smooth", delay = 100 } = options;
  const elementRef = useRef<T>(null);

  const handleFocus = useCallback(() => {
    if (!elementRef.current) return;

    // Small delay to allow keyboard animation to start
    setTimeout(() => {
      const element = elementRef.current;
      if (!element) return;

      const scrollableParent = findScrollableParent(element);
      if (!scrollableParent) return;

      // Check if the parent is actually scrollable
      const isScrollable = scrollableParent.scrollHeight > scrollableParent.clientHeight;
      if (!isScrollable) return;

      // Get element position relative to the scrollable container
      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollableParent.getBoundingClientRect();

      // Calculate the current scroll offset of the element relative to container
      const elementTop = elementRect.top - containerRect.top + scrollableParent.scrollTop;

      // Calculate target scroll position (element at top 1/3 of viewport)
      const viewportHeight = window.innerHeight;
      const targetScrollTop = elementTop - viewportHeight * topRatio;

      // Scroll to position
      scrollableParent.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior,
      });
    }, delay);
  }, [topRatio, behavior, delay]);

  return { elementRef, handleFocus };
};

/**
 * Find the nearest scrollable parent element
 */
function findScrollableParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;

  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    const isScrollable =
      (overflow === "auto" ||
        overflow === "scroll" ||
        overflowY === "auto" ||
        overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight;

    if (isScrollable) {
      return parent;
    }

    parent = parent.parentElement;
  }

  // If no scrollable parent found, return document.documentElement (page scroll)
  return document.documentElement;
}
